import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, viewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { map, Subject, switchMap, tap } from 'rxjs';

import {
    AgendaItemImportedVoteResponseModel as AgendaItemImportedVoteModel,
    AgendaItemImportedVoteResponseModel
} from '../../../models/agenda-item-imported-vote-model.model';
import { AgendaItemModel } from '../../../models/agenda-item.model';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { PostValueResponseModel } from '../../../models/core/post-value-response.model';
import { VoteOptionEnum } from '../../../models/enums/vote-option.enum';
import { OptionalType } from '../../../models/types/optional.type';
import { ErrorsService } from '../../../services/errors.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { InputComponent } from '../../forms/input/input.component';
import { ImportTypesEnum } from './import-types.enum';

interface FormGroupType {
    importType: FormControl<ImportTypesEnum>;
    vote: FormControl<VoteOptionEnum>;
    barcode?: FormControl<string>;
    importFile?: FormControl<Blob | null>;
    importFileContent?: FormControl<string[] | null>;
}

@Component({
    selector: 'sl-agenda-item-import-votes',
    templateUrl: './agenda-item-import-votes.component.html',
    styleUrls: ['./agenda-item-import-votes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgendaItemImportVotesComponent implements OnInit, OnDestroy {
    readonly barcodeField = viewChild<InputComponent>('barcodeField');

    readonly displayedColumns = ['successful', 'barcode', 'message', 'voteOption', 'resetVoteButton'];

    readonly VoteOptionEnum = VoteOptionEnum;
    readonly ImportTypesEnum = ImportTypesEnum;

    readonly shouldRefreshAfterClose = signal(false);
    readonly isVotingOpen = computed(() => this.agendaItem().isVotingOpen);
    readonly generalMeetingId = computed(() => this.agendaItem().generalMeetingId);
    readonly votingCouponNumber = computed(() => this.agendaItem().votingCouponNumber);

    // voting not open state
    readonly startVotingFormGroup = new FormGroup({
        couponNr: new FormControl<OptionalType<number>>(undefined),
        defaultVote: new FormControl<VoteOptionEnum>(VoteOptionEnum.NoVote, { nonNullable: true, validators: Validators.required })
    });
    readonly isStartingVote = signal(false);

    // voting open state
    readonly isImporting = signal(false);
    readonly importedVotesObservable: Subject<AgendaItemImportedVoteModel[]> = new Subject();
    readonly formGroup = new FormGroup<FormGroupType>({
        importType: new FormControl(ImportTypesEnum.Barcode, { nonNullable: true, validators: Validators.required }),
        vote: new FormControl(VoteOptionEnum.NoVote, { nonNullable: true, validators: Validators.required })
    });

    private importedVotes: AgendaItemImportedVoteModel[] = [];
    private barcodes: string[];
    private readonly agendaItem: WritableSignal<AgendaItemModel>;

    private readonly data = inject<{ agendaItem: AgendaItemModel }>(MAT_DIALOG_DATA);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly errorsService = inject(ErrorsService);

    constructor() {
        this.agendaItem = signal(this.data.agendaItem);
    }

    ngOnInit(): void {
        if (this.isVotingOpen()) {
            this.errorsService.shouldDisplayAlertOnError = false;
        }
        this.importTypeChanged();
    }

    ngOnDestroy(): void {
        this.errorsService.shouldDisplayAlertOnError = true;
    }

    openVoting(): void {
        if (this.isVotingOpen()) {
            return;
        }

        this.startVotingFormGroup.updateValueAndValidity();
        this.startVotingFormGroup.markAllAsTouched();
        if (!this.startVotingFormGroup.valid) {
            return;
        }

        const defaultVote = this.startVotingFormGroup.getRawValue().defaultVote;
        const couponNr = this.startVotingFormGroup.getRawValue().couponNr || undefined;

        this.isStartingVote.set(true);
        this.generalMeetingsResource
            .startVotingOnAgendaItem(this.agendaItem().gmAgendaItemNumber, defaultVote, couponNr)
            .pipe(
                switchMap(() => this.generalMeetingsResource.getCurrentAgendaItems()),
                map(result => result.value?.find(item => item.gmAgendaItemNumber === this.agendaItem().gmAgendaItemNumber)),
                tap(updatedItem => {
                    if (updatedItem) {
                        this.agendaItem.set(updatedItem);
                        this.errorsService.shouldDisplayAlertOnError = false;
                    }

                    this.shouldRefreshAfterClose.set(true);
                })
            )
            .subscribe()
            .add(() => this.isStartingVote.set(false));
    }

    importTypeChanged(): void {
        // eslint-disable-next-line eqeqeq
        if (this.formGroup.value.importType == ImportTypesEnum.Barcode) {
            this.formGroup.removeControl('importFile');
            this.formGroup.removeControl('importFile');
            this.formGroup.removeControl('importFileContent');
            this.formGroup.addControl('barcode', new FormControl('', { nonNullable: true, validators: Validators.required }));
        } else {
            this.formGroup.removeControl('barcode');
            this.formGroup.addControl(
                'importFile',
                // eslint-disable-next-line no-null/no-null
                new FormControl(null, { validators: Validators.required })
            );
            this.formGroup.addControl(
                'importFileContent',
                // eslint-disable-next-line no-null/no-null
                new FormControl(null, { validators: Validators.required })
            );
            // eslint-disable-next-line eqeqeq
            if (this.formGroup.value.vote == VoteOptionEnum.NoVote) {
                this.formGroup.patchValue({ vote: VoteOptionEnum.Yes });
            }
        }
    }

    fileSelected(files: Blob[]): void {
        // eslint-disable-next-line no-null/no-null
        this.formGroup.patchValue({ importFileContent: null });
        this.barcodes = [];
        const reader = new FileReader();
        if (files && files.length > 0) {
            const file = files[0];
            reader.onloadend = () => {
                const fileContent = (reader.result as string).split(/[\r\n]/);
                for (const line of fileContent) {
                    const barcode = line.trim();
                    if (barcode.length === 0) {
                        continue;
                    }
                    this.barcodes.push(barcode);
                }
                this.formGroup.patchValue({ importFileContent: fileContent });
            };
            reader.readAsText(file);
        }
    }

    async submit(): Promise<void> {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAllAsTouched();
        if (!this.formGroup.valid) {
            return;
        }

        this.formGroup.disable();
        this.isImporting.set(true);

        if (this.formGroup.value.importType === ImportTypesEnum.Barcode) {
            const barcode = this.formGroup.value.barcode || '';
            this.barcodes = [barcode];
        }

        // Import barcodes
        const vote = this.formGroup.value.vote;
        if (vote === undefined) {
            this.isImporting.set(false);
            this.formGroup.enable();
            return;
        }

        this.generalMeetingsResource.importVotes(this.agendaItem().gmAgendaItemNumber, this.barcodes, vote).subscribe({
            next: this.handleImportSuccess.bind(this),
            error: this.handleImportError.bind(this)
        });
    }

    handleImportSuccess(res: ApiResponseModel<PostValueResponseModel<AgendaItemImportedVoteResponseModel[]>>): void {
        this.handleImportResponse(res.value);
    }

    handleImportError(err: { error: ApiResponseModel<PostValueResponseModel<AgendaItemImportedVoteResponseModel[]>> }): void {
        this.handleImportResponse(err.error.value);
    }

    handleImportResponse(res: PostValueResponseModel<AgendaItemImportedVoteResponseModel[]>): void {
        this.isImporting.set(false);

        this.importedVotes.unshift(...res.result.reverse());
        this.importedVotesObservable.next(this.importedVotes);

        this.formGroup.enable();

        // Reset field
        this.formGroup.controls.importFile?.reset();
        this.formGroup.controls.importFileContent?.reset();
        this.formGroup.controls.barcode?.reset();
        // eslint-disable-next-line no-null/no-null
        this.formGroup.controls.barcode?.setErrors(null);

        // Focus barcode field
        this.barcodeField()?.inputField?.nativeElement?.focus();
    }

    async resetVote(vote: AgendaItemImportedVoteModel): Promise<void> {
        // Import barcodes
        this.generalMeetingsResource.resetVote(this.agendaItem().gmAgendaItemNumber, vote.shareholderId).subscribe({
            next: this.handleResetVoteSuccess(vote.shareholderId).bind(this)
        });
    }

    handleResetVoteSuccess(shareholderId: number) {
        return () => {
            for (let i = this.importedVotes.length - 1; i >= 0; i--) {
                const element = this.importedVotes[i];
                if (element.shareholderId === shareholderId) {
                    this.importedVotes.splice(i, 1);
                }
            }
            this.importedVotesObservable.next(this.importedVotes);
        };
    }
}
